<template>
  <div class="relative" ref="dropdown">
    <div
      class="flex items-center w-full px-1 py-1.5 pl-3 border rounded-lg shadow-sm focus:outline-none focus:shadow-none"
      :class="[
        { 'pointer-events-none bg-gray-100 text-gray-500': disabled },
        { 'border-red-500 text-red-500 bg-red-50': error },
        { 'border-transparent shadow-none': borderless },
      ]"
      @click="dropdownOpen = !dropdownOpen">
      <div class="flex items-center w-full px-1 text-gray-900 truncate">
        <slot v-if="listItem" name="selected-option" v-bind="listItem">{{
          getItemText(listItem)
        }}</slot>
        <span v-else class="text-gray-500">{{ placeholder }}</span>
      </div>

      <button
        v-if="listItem && clearable"
        type="button"
        @click.stop="onClear"
        class="text-gray-300 transition-all border-r border-gray-200 outline-none cursor-pointer focus:outline-none hover:text-gray-600"
        :class="{ 'border-red-500 text-red-500 ': error }">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="w-4 h-4 mx-2 fill-current"
          viewBox="0 0 24 24">
          <path
            fill="currentColor"
            d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" />
        </svg>
      </button>
      <button
        type="button"
        class="text-gray-300 transition-all outline-none cursor-pointer focus:outline-none hover:text-gray-600"
        :class="[{ 'border-red-500 text-red-500 ': error }, { 'rotate-180': dropdownOpen }]">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="w-6 h-6 mx-2 fill-current"
          viewBox="0 0 24 24">
          <path fill="currentColor" d="M7.41 8.58L12 13.17l4.59-4.59L18 10l-6 6l-6-6l1.41-1.42Z" />
        </svg>
      </button>
    </div>
    <div
      v-if="dropdownOpen"
      :class="[
        'absolute z-50 flex flex-col w-full bg-white border border-gray-200 rounded shadow max-h-[200px] overflow-auto',
        direction === 'up' ? 'bottom-full mb-2' : 'top-full mt-1',
      ]"
      v-bind="containerProps"
      id="dropdownSearch">
      <div
        class="sticky top-0 left-0 z-20 flex items-center gap-2 p-3 bg-gray-100"
        v-if="searchable">
        <label for="input-group-search" class="sr-only">{{ $t('common.actions.search') }}</label>
        <div class="relative w-full">
          <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <svg
              class="w-4 h-4 text-gray-500 dark:text-gray-400"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 20">
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z" />
            </svg>
          </div>
          <input
            v-model="searchTerm"
            type="text"
            id="input-group-search"
            class="block w-full p-2 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-theme-500 focus:border-theme-500"
            :placeholder="$t('common.actions.search')" />
        </div>
        <slot name="searchOption" :onClose="onSearchFunctionClick"></slot>
      </div>
      <ul
        class="z-10 pb-3 text-sm text-gray-700 bg-white"
        aria-labelledby="dropdownSearchButton"
        v-bind="wrapperProps">
        <li
          v-for="{ index, data } in list"
          :key="index"
          @click="onOptionSelect(data)"
          class="h-[42px]">
          <div
            class="flex items-center p-2 rounded hover:bg-gray-100"
            :class="{ 'bg-sky-100': data == listItem }">
            <slot name="option" v-bind="data">{{ getItemText(data) }}</slot>
          </div>
        </li>
      </ul>
    </div>
    <p class="mx-2 my-1 text-xs text-red-500" v-show="error">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
import { useVirtualList } from '@vueuse/core';
import { ref, computed } from 'vue';
import { onClickOutside } from '@vueuse/core';

const props = defineProps<{
  options: any[];
  itemText?: string;
  returnItem?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  clearable?: boolean;
  searchable?: boolean;
  borderless?: boolean;
  direction?: 'up' | 'down';
}>();

const listItem = defineModel<any>();

const dropdown = ref(null);

const emit = defineEmits(['onChange']);

const getItemText = (option: any) => {
  return option && props.itemText ? option[props.itemText] : option;
};

const dropdownOpen = ref(false);

onClickOutside(dropdown, () => (dropdownOpen.value = false));

function onClear() {
  listItem.value = null;
  searchTerm.value = '';
  emit('onChange', null);
}

const searchTerm = ref('');

const filteredList = computed(() => {
  return props.options.filter((item) => {
    const searchTermLowercase = searchTerm.value.toLowerCase();
    if (props.itemText) {
      return item[props.itemText].toLowerCase().includes(searchTermLowercase);
    } else return item.toString().toLowerCase().includes(searchTermLowercase);
  });
});

const { list, containerProps, wrapperProps } = useVirtualList(filteredList, {
  itemHeight: 42,
});

function onOptionSelect(option: any) {
  if (props.returnItem) listItem.value = option[props.returnItem];
  else listItem.value = option;
  emit('onChange', option);
  dropdownOpen.value = false;
}

function onSearchFunctionClick() {
  dropdownOpen.value = false;
}
</script>
